import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Loader2, FileText, Upload, Users, ShieldCheck, X, CheckCircle2 } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { toast } from "sonner";

const ESTADOS_MX = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","CDMX","Chiapas","Chihuahua",
  "Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero","Hidalgo","Jalisco",
  "Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla","Querétaro","Quintana Roo",
  "San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala","Veracruz","Yucatán","Zacatecas",
];

const empty = {
  razon_social: "", nombre_comercial: "", tipo_persona: "moral", es_externo: true, rfc: "",
  domicilio: { calle: "", numero: "", colonia: "", cp: "", municipio: "", estado: "", pais: "México" },
  telefonos: [""], correos: [""],
  socios: [""],
  personas_ejecutoras: [{ nombre: "", rol: "Gestor telefónico", telefono: "" }],
  contrato_fecha_inicio: "", contrato_fecha_fin: "",
  fecha_alta: "", notas: "",
};

export function DespachosTab({ can }) {
  const puedeEditar = can("cobranza", "editar") || can("cobranza", "crear");
  const [items, setItems] = useState([]);
  const [legacy, setLegacy] = useState(null);
  const [padron, setPadron] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(async () => {
    const [lst, leg, pad] = await Promise.all([
      api.get("/despachos-cobranza").then((r) => r.data).catch(() => []),
      api.get("/cobranza/gestores-legacy").then((r) => r.data).catch(() => null),
      api.get("/despachos-cobranza/padron/estatus").then((r) => r.data).catch(() => null),
    ]);
    setItems(lst);
    setLegacy(leg);
    setPadron(pad);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const openAlta = () => { setEditing(null); setForm(empty); setDlgOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...empty, ...row,
      domicilio: row.domicilio || empty.domicilio,
      telefonos: row.telefonos?.length ? row.telefonos : [""],
      correos: row.correos?.length ? row.correos : [""],
      socios: row.socios?.length ? row.socios : [""],
      personas_ejecutoras: row.personas_ejecutoras?.length ? row.personas_ejecutoras : [{ nombre: "", rol: "", telefono: "" }],
    });
    setDlgOpen(true);
  };

  const guardar = async () => {
    if (!form.razon_social.trim()) { toast.error("Razón social es obligatoria"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        telefonos: form.telefonos.filter((x) => x?.trim()),
        correos: form.correos.filter((x) => x?.trim()),
        socios: form.socios.filter((x) => x?.trim()),
        personas_ejecutoras: form.personas_ejecutoras.filter((p) => p.nombre?.trim()),
      };
      if (editing?.id) {
        await api.patch(`/despachos-cobranza/${editing.id}`, payload);
        toast.success("Despacho actualizado");
      } else {
        await api.post("/despachos-cobranza", payload);
        toast.success(form.es_externo ? "Despacho EXTERNO dado de alta (aplica REDECO)" : "Gestor INTERNO dado de alta (no entra a REDECO)");
      }
      setDlgOpen(false);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const confirmarPadron = async (sinCambios) => {
    setBusy(true);
    try {
      await api.post("/despachos-cobranza/padron/confirmar", { sin_cambios: sinCambios });
      toast.success(`Padrón confirmado (${sinCambios ? "sin cambios" : "con edición"})`);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo confirmar");
    } finally { setBusy(false); }
  };

  const subirContrato = async (despacho_id, file) => {
    const fd = new FormData();
    fd.append("archivo", file);
    fd.append("tipo", "contrato");
    try {
      await api.post(`/despachos-cobranza/${despacho_id}/documento`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contrato adjuntado (cifrado)");
      const upd = await api.get(`/despachos-cobranza/${despacho_id}`).then((r) => r.data);
      setDetalle(upd);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir");
    }
  };

  const COLS = [
    { key: "razon_social", label: "Razón social", render: (r) => (
      <div><span className="block font-medium text-slate-800">{r.razon_social}</span>
      {r.nombre_comercial && <span className="text-[11px] text-slate-500">{r.nombre_comercial}</span>}</div>
    )},
    { key: "tipo_persona", label: "Tipo", render: (r) => r.tipo_persona === "moral" ? "P. Moral" : "P. Física" },
    { key: "rfc", label: "RFC" },
    { key: "es_externo", label: "REDECO", render: (r) => r.es_externo
      ? <span data-testid={`badge-externo-${r.id}`} className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Externo · aplica</span>
      : <span data-testid={`badge-interno-${r.id}`} className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Interno · no aplica</span> },
    { key: "personas_ejecutoras", label: "Ejecutores", render: (r) => (r.personas_ejecutoras?.length || 0) },
    { key: "contrato_fecha_fin", label: "Contrato hasta", render: (r) => r.contrato_fecha_fin ? fdate(r.contrato_fecha_fin) : "—" },
    { key: "activo", label: "Activo", render: (r) => r.activo ? "Sí" : "No" },
    { key: "ultima_confirmacion_padron", label: "Padrón confirmado", render: (r) => r.ultima_confirmacion_padron ? fdate(r.ultima_confirmacion_padron) : <span className="text-amber-600">Pendiente</span> },
  ];

  return (
    <div className="space-y-4 py-4">
      {/* Padrón mensual (REDECO H7) */}
      {padron && (
        <div data-testid="padron-mensual-card" className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Padrón REDECO — {padron.mes_actual}</p>
              <p className="mt-1 text-sm text-slate-700">
                {padron.despachos_externos_activos} despacho(s) externo(s) activo(s).{" "}
                {padron.confirmado_este_mes
                  ? <span data-testid="padron-confirmado" className="text-emerald-700"><CheckCircle2 className="mr-1 inline h-4 w-4" />Confirmado este mes por {padron.confirmacion?.confirmado_por}</span>
                  : <span data-testid="padron-pendiente" className="text-amber-700">Pendiente de confirmación mensual</span>}
              </p>
            </div>
            {puedeEditar && !padron.confirmado_este_mes && (
              <div className="flex gap-2">
                <Button data-testid="btn-padron-sin-cambios" variant="outline" size="sm" onClick={() => confirmarPadron(true)} disabled={busy}>
                  Confirmar sin cambios
                </Button>
                <Button data-testid="btn-padron-con-cambios" size="sm" onClick={() => confirmarPadron(false)} disabled={busy}
                  className="bg-primary hover:bg-sidebar-hover">
                  Marcar como revisado
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="kpi-despachos-total" label="Despachos registrados" value={items.length} icon="Building2" accent="primary" />
        <StatCard testid="kpi-despachos-externos" label="Externos (REDECO)" value={items.filter((i) => i.es_externo && i.activo).length} icon="ShieldAlert" accent="amber" />
        <StatCard testid="kpi-despachos-internos" label="Internos" value={items.filter((i) => !i.es_externo && i.activo).length} icon="Users" accent="green" />
        <StatCard testid="kpi-gestores-legacy" label="Gestores texto libre (migrar)"
          value={legacy?.total_gestiones_texto_libre ?? 0} icon="AlertTriangle" accent="red" />
      </div>

      {/* Tabla */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-semibold text-slate-700">Catálogo de despachos</h3>
        {puedeEditar && (
          <Button data-testid="btn-nuevo-despacho" size="sm" onClick={openAlta} className="bg-primary hover:bg-sidebar-hover">
            <Plus className="mr-1 h-4 w-4" /> Nuevo despacho
          </Button>
        )}
      </div>
      <DataTable testid="tabla-despachos" columns={COLS} rows={items} onRowClick={(r) => setDetalle(r)}
        emptyLabel="Sin despachos registrados. Da de alta el primero para cumplir REDECO." />

      {/* Legacy (texto libre) */}
      {legacy && (legacy.gestiones?.length > 0 || legacy.asignaciones?.length > 0) && (
        <div data-testid="legacy-panel" className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900">
          <p className="font-semibold">Gestores en texto libre pendientes de vincular (H1d)</p>
          <p className="mt-1 text-xs">
            Da de alta el despacho y luego vincula manualmente al asignar/registrar la gestión.
          </p>
          <ul className="mt-2 text-xs">
            {(legacy.gestiones || []).slice(0, 10).map((g) => (
              <li key={g.nombre} data-testid={`legacy-item-${g.nombre.replace(/\s+/g, "-")}`}>
                • <b>{g.nombre}</b> — {g.gestiones} gestiones (última {g.ultima || "—"})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dialog alta/edición */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar despacho" : "Nuevo despacho de cobranza"}</DialogTitle>
            <DialogDescription>
              REDECO exige razón social, RFC, domicilio, teléfonos, socios (si PM) y personas físicas ejecutoras.
              Marca &quot;Externo&quot; para que aplique el registro CONDUSEF.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <ShieldCheck className="h-5 w-5 text-amber-700" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-900">¿Es despacho externo (tercero)?</p>
                <p className="text-[11px] text-amber-800">Solo los externos entran al padrón REDECO y al reporte trimestral CONDUSEF.</p>
              </div>
              <Switch data-testid="sw-es-externo" checked={form.es_externo}
                onCheckedChange={(v) => setForm({ ...form, es_externo: v })} />
            </div>

            <div className="md:col-span-2">
              <Label>Razón social o nombre *</Label>
              <Input data-testid="in-razon-social" value={form.razon_social}
                onChange={(e) => setForm({ ...form, razon_social: e.target.value })} />
            </div>
            <div>
              <Label>Nombre comercial</Label>
              <Input data-testid="in-nombre-comercial" value={form.nombre_comercial}
                onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} />
            </div>
            <div>
              <Label>Tipo de persona</Label>
              <Select value={form.tipo_persona} onValueChange={(v) => setForm({ ...form, tipo_persona: v })}>
                <SelectTrigger data-testid="sel-tipo-persona"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="moral">Persona Moral</SelectItem>
                  <SelectItem value="fisica">Persona Física</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>RFC</Label>
              <Input data-testid="in-rfc" value={form.rfc}
                onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>Fecha de alta</Label>
              <Input type="date" data-testid="in-fecha-alta" value={form.fecha_alta}
                onChange={(e) => setForm({ ...form, fecha_alta: e.target.value })} />
            </div>

            {/* Contrato */}
            <div>
              <Label>Contrato — inicio</Label>
              <Input type="date" data-testid="in-contrato-ini" value={form.contrato_fecha_inicio}
                onChange={(e) => setForm({ ...form, contrato_fecha_inicio: e.target.value })} />
            </div>
            <div>
              <Label>Contrato — fin</Label>
              <Input type="date" data-testid="in-contrato-fin" value={form.contrato_fecha_fin}
                onChange={(e) => setForm({ ...form, contrato_fecha_fin: e.target.value })} />
            </div>

            {/* Domicilio */}
            <div className="md:col-span-2">
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Domicilio</Label>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                <Input placeholder="Calle" data-testid="in-dom-calle"
                  className="md:col-span-3" value={form.domicilio.calle}
                  onChange={(e) => setForm({ ...form, domicilio: { ...form.domicilio, calle: e.target.value } })} />
                <Input placeholder="Número" data-testid="in-dom-numero"
                  value={form.domicilio.numero}
                  onChange={(e) => setForm({ ...form, domicilio: { ...form.domicilio, numero: e.target.value } })} />
                <Input placeholder="Colonia" data-testid="in-dom-colonia"
                  className="md:col-span-2" value={form.domicilio.colonia}
                  onChange={(e) => setForm({ ...form, domicilio: { ...form.domicilio, colonia: e.target.value } })} />
                <Input placeholder="CP" data-testid="in-dom-cp"
                  value={form.domicilio.cp}
                  onChange={(e) => setForm({ ...form, domicilio: { ...form.domicilio, cp: e.target.value } })} />
                <Input placeholder="Municipio/Alcaldía" data-testid="in-dom-mun"
                  className="md:col-span-2" value={form.domicilio.municipio}
                  onChange={(e) => setForm({ ...form, domicilio: { ...form.domicilio, municipio: e.target.value } })} />
                <Select value={form.domicilio.estado} onValueChange={(v) => setForm({ ...form, domicilio: { ...form.domicilio, estado: v } })}>
                  <SelectTrigger data-testid="sel-dom-estado" className="md:col-span-3"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {ESTADOS_MX.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Teléfonos */}
            <div className="md:col-span-2">
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Teléfonos usados para cobranza</Label>
              {form.telefonos.map((t, i) => (
                <div key={i} className="mb-1 flex gap-2">
                  <Input data-testid={`in-tel-${i}`} placeholder="+52 55 0000 0000" value={t}
                    onChange={(e) => { const arr = [...form.telefonos]; arr[i] = e.target.value; setForm({ ...form, telefonos: arr }); }} />
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, telefonos: form.telefonos.filter((_, j) => j !== i) })}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button data-testid="btn-add-tel" size="sm" variant="outline"
                onClick={() => setForm({ ...form, telefonos: [...form.telefonos, ""] })}>
                <Plus className="mr-1 h-3 w-3" /> Agregar teléfono
              </Button>
            </div>

            {/* Socios (solo PM) */}
            {form.tipo_persona === "moral" && (
              <div className="md:col-span-2">
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Socios (PM)</Label>
                {form.socios.map((s, i) => (
                  <div key={i} className="mb-1 flex gap-2">
                    <Input data-testid={`in-socio-${i}`} placeholder="Nombre del socio" value={s}
                      onChange={(e) => { const arr = [...form.socios]; arr[i] = e.target.value; setForm({ ...form, socios: arr }); }} />
                    <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, socios: form.socios.filter((_, j) => j !== i) })}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button data-testid="btn-add-socio" size="sm" variant="outline"
                  onClick={() => setForm({ ...form, socios: [...form.socios, ""] })}>
                  <Plus className="mr-1 h-3 w-3" /> Agregar socio
                </Button>
              </div>
            )}

            {/* Personas ejecutoras */}
            <div className="md:col-span-2">
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Personas físicas que ejecutan la cobranza *</Label>
              {form.personas_ejecutoras.map((p, i) => (
                <div key={i} className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-4">
                  <Input data-testid={`in-pe-nombre-${i}`} placeholder="Nombre completo" className="md:col-span-2" value={p.nombre}
                    onChange={(e) => { const arr = [...form.personas_ejecutoras]; arr[i] = { ...p, nombre: e.target.value }; setForm({ ...form, personas_ejecutoras: arr }); }} />
                  <Input data-testid={`in-pe-rol-${i}`} placeholder="Rol (telefónico / visita)" value={p.rol}
                    onChange={(e) => { const arr = [...form.personas_ejecutoras]; arr[i] = { ...p, rol: e.target.value }; setForm({ ...form, personas_ejecutoras: arr }); }} />
                  <Input data-testid={`in-pe-tel-${i}`} placeholder="Teléfono" value={p.telefono}
                    onChange={(e) => { const arr = [...form.personas_ejecutoras]; arr[i] = { ...p, telefono: e.target.value }; setForm({ ...form, personas_ejecutoras: arr }); }} />
                </div>
              ))}
              <Button data-testid="btn-add-persona" size="sm" variant="outline"
                onClick={() => setForm({ ...form, personas_ejecutoras: [...form.personas_ejecutoras, { nombre: "", rol: "", telefono: "" }] })}>
                <Plus className="mr-1 h-3 w-3" /> Agregar persona
              </Button>
            </div>

            <div className="md:col-span-2">
              <Label>Notas internas</Label>
              <Textarea data-testid="in-notas" rows={2} value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-despacho" onClick={guardar} disabled={busy}
              className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editing ? "Guardar cambios" : "Dar de alta"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog detalle */}
      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detalle && (
            <>
              <DialogHeader>
                <DialogTitle>{detalle.razon_social}</DialogTitle>
                <DialogDescription>
                  {detalle.es_externo ? "Despacho externo (aplica REDECO)" : "Gestor interno (no aplica REDECO)"}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Tipo:</span> {detalle.tipo_persona === "moral" ? "P. Moral" : "P. Física"}</div>
                <div><span className="text-slate-500">RFC:</span> {detalle.rfc || "—"}</div>
                <div className="col-span-2">
                  <span className="text-slate-500">Domicilio:</span> {[detalle.domicilio?.calle, detalle.domicilio?.numero, detalle.domicilio?.colonia, detalle.domicilio?.municipio, detalle.domicilio?.estado, detalle.domicilio?.cp].filter(Boolean).join(", ") || "—"}
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Teléfonos:</span> {(detalle.telefonos || []).filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Personas ejecutoras:</span>
                  <ul className="ml-4 mt-1 list-disc text-sm">
                    {(detalle.personas_ejecutoras || []).map((p) => (
                      <li key={p.id || p.nombre} data-testid={`pe-item-${p.id || p.nombre}`}>
                        <b>{p.nombre}</b>{p.rol ? ` — ${p.rol}` : ""}{p.telefono ? ` · ${p.telefono}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
                {detalle.tipo_persona === "moral" && (
                  <div className="col-span-2">
                    <span className="text-slate-500">Socios:</span> {(detalle.socios || []).filter(Boolean).join(" · ") || "—"}
                  </div>
                )}
                <div className="col-span-2">
                  <span className="text-slate-500">Contrato:</span>{" "}
                  {detalle.contrato_fecha_inicio || "—"} → {detalle.contrato_fecha_fin || "—"}
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Documentos adjuntos (cifrados):</span>
                  <ul className="ml-4 mt-1 list-disc text-sm">
                    {(detalle.documentos || []).map((d) => (
                      <li key={d.id} data-testid={`doc-adj-${d.id}`}>
                        <a className="text-primary underline" href={`${(api.defaults.baseURL || "")}/despachos-cobranza/${detalle.id}/documento/${d.id}`}
                          target="_blank" rel="noreferrer"><FileText className="mr-1 inline h-3 w-3" />{d.filename}</a>
                        <span className="ml-2 text-[11px] text-slate-500">{d.tipo} · {(d.tamano / 1024).toFixed(1)} KB · sha256:{d.sha256?.slice(0, 12)}…</span>
                      </li>
                    ))}
                    {(!detalle.documentos || detalle.documentos.length === 0) && <li className="text-slate-400">Sin documentos</li>}
                  </ul>
                  {puedeEditar && (
                    <div className="mt-2">
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">
                        <Upload className="h-3 w-3" /> Subir contrato / documento
                        <input data-testid="in-file-contrato" type="file" className="hidden"
                          onChange={(e) => e.target.files?.[0] && subirContrato(detalle.id, e.target.files[0])} />
                      </label>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                {puedeEditar && <Button data-testid="btn-editar-despacho" variant="outline" onClick={() => { setDetalle(null); openEdit(detalle); }}>Editar</Button>}
                <Button onClick={() => setDetalle(null)}>Cerrar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
